import { useState } from "react";

/**
 * Generic CityFAQ, renders a per-city FAQ accordion AND emits FAQPage JSON-LD
 * (eligible for Google rich snippets).
 *
 * Usage:
 *   <CityFAQ city={city} faq={CITY_FAQ_DATA[slug]} />
 *
 * To get the JSON-LD outside this component:
 *   import { buildCityFAQSchema } from "@/components/CityFAQ";
 *   const ld = buildCityFAQSchema(faq);
 */

export const buildCityFAQSchema = (faqArray) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (faqArray || []).map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: { "@type": "Answer", text: item.a },
  })),
});

const CityFAQ = ({ city, faq }) => {
  const [openIndex, setOpenIndex] = useState(0);
  if (!faq || faq.length === 0) return null;

  return (
    <section
      className="py-20 lg:py-28 bg-cream"
      data-testid={`city-faq-${city.slug}`}
    >
      <div className="container-tm max-w-4xl">
        <div className="mb-14">
          <span className="overline">
            Questions fréquentes, Conciergerie {city.name}
          </span>
          <h2 className="mt-5 font-serif font-light text-ink text-4xl md:text-5xl lg:text-[52px] leading-[1.04] tracking-tight">
            Tout ce que les propriétaires
            <span className="italic text-gold"> {city.name === "Cancale" ? "Cancalais" : "à " + city.name}</span> nous demandent.
          </h2>
        </div>

        <div className="space-y-3">
          {faq.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={item.q}
                data-testid={`city-faq-${city.slug}-${i}`}
                className={`bg-white border rounded-2xl overflow-hidden transition-all ${
                  isOpen ? "border-gold/40 shadow-soft" : "border-gold/15"
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-start justify-between gap-4 text-left px-6 lg:px-8 py-5 lg:py-6 hover:bg-cream/30 transition-colors"
                  aria-expanded={isOpen}
                >
                  <p className="font-serif text-lg lg:text-xl text-ink leading-snug">
                    {item.q}
                  </p>
                  <span
                    className={`w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0 mt-1 transition-transform ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  >
                    <span className="text-gold text-xl leading-none font-light">
                      +
                    </span>
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 lg:px-8 pb-6 lg:pb-8">
                    <p className="font-sans text-sm md:text-base text-ink/75 leading-relaxed border-t border-gold/15 pt-5">
                      {item.a}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CityFAQ;
